import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCart, addOrUpdateCartItem, removeCartItem as removeCartItemApi } from '../utils/cartApi';
import { auth, db } from '../firebase';
import { collection, getDocs, deleteDoc } from 'firebase/firestore';
import { trackAddToCart } from '../lib/analyticsService';
import {
  getGuestCart,
  saveGuestCart,
  addToGuestCart,
  updateGuestCartItem,
  removeFromGuestCart,
  clearGuestCart,
  isLocalStorageAvailable,
} from '../utils/guestCart';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [justCleared, setJustCleared] = useState(false);

  // Fetch cart from Firestore (authenticated) or localStorage (guest)
  const fetchCart = useCallback(async () => {
    console.log('[CartContext] fetchCart called');
    setLoading(true);
    setError('');
    
    const user = auth.currentUser;
    
    try {
      if (user) {
        // Authenticated user - fetch from Firestore
        let items = await getCart();
        console.log('[CartContext] Fetched', items.length, 'cart items from Firestore');
        items = items.map(item => ({ ...item, qty: Number(item.qty) > 0 ? Number(item.qty) : 1 }));
        setCartItems(items);
        console.log('[CartContext] Cart fetched successfully from Firestore');
      } else {
        // Guest user - fetch from localStorage
        const items = getGuestCart();
        console.log('[CartContext] Fetched', items.length, 'cart items from localStorage');
        setCartItems(items);
        console.log('[CartContext] Guest cart fetched successfully');
      }
    } catch (err) {
      setError('Failed to load cart');
      console.error('[CartContext] Error fetching cart:', err);
      // Fallback to empty cart on error
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add or update item
  const updateCartItem = async (itemId, newQty) => {
    try {
      const item = cartItems.find(i => i.id === itemId);
      if (!item) return;
      
      const user = auth.currentUser;
      const safeQty = Number(newQty) > 0 ? Number(newQty) : 1;
      const updated = { ...item, qty: safeQty };
      
      if (user) {
        // Authenticated user - update in Firestore
        await addOrUpdateCartItem(updated);
      } else {
        // Guest user - update in localStorage
        updateGuestCartItem(itemId, safeQty);
      }
      
      setCartItems(items => items.map(i => i.id === itemId ? updated : i));
    } catch (err) {
      setError('Failed to update cart');
      console.error('[CartContext] Error updating cart item:', err);
    }
  };

  // Add item (or update if exists)
  const addCartItem = async (item) => {
    try {
      const user = auth.currentUser;
      const existing = cartItems.find(i => i.id === item.id);
      const itemQty = Number(item.qty) > 0 ? Number(item.qty) : 1;
      const newQty = existing ? (Number(existing.qty) > 0 ? Number(existing.qty) + itemQty : itemQty) : itemQty;
      const newItem = existing ? { ...existing, qty: newQty } : { ...item, qty: itemQty };
      
      // Track add to cart event for analytics (non-blocking)
      if (item.sellerId) {
        try {
          const sessionId = sessionStorage.getItem('vesta_session_id') || 'unknown';
          await trackAddToCart(item.id, item.sellerId, sessionId, user?.uid);
        } catch (error) {
          console.warn('⚠️ Analytics tracking failed (non-critical):', error.message);
        }
      }
      
      if (user) {
        // Authenticated user - save to Firestore
        await addOrUpdateCartItem(newItem);
      } else {
        // Guest user - save to localStorage
        addToGuestCart(newItem);
      }
      
      setCartItems(items => {
        const idx = items.findIndex(i => i.id === item.id);
        if (idx !== -1) {
          const updated = [...items];
          updated[idx] = newItem;
          return updated;
        } else {
          return [...items, newItem];
        }
      });
    } catch (err) {
      setError('Failed to add to cart');
      console.error('[CartContext] Error adding to cart:', err);
    }
  };

  // Remove item
  const removeCartItem = async (itemId) => {
    try {
      const user = auth.currentUser;
      
      if (user) {
        // Authenticated user - remove from Firestore
        await removeCartItemApi(itemId);
      } else {
        // Guest user - remove from localStorage
        removeFromGuestCart(itemId);
      }
      
      setCartItems(items => items.filter(i => i.id !== itemId));
    } catch (err) {
      setError('Failed to remove from cart');
      console.error('[CartContext] Error removing from cart:', err);
    }
  };

  // Merge guest cart into user cart when user logs in
  const mergeGuestCart = useCallback(async () => {
    const guestCart = getGuestCart();
    if (guestCart.length === 0) return;
    
    try {
      console.log('[CartContext] Merging', guestCart.length, 'guest cart items');
      
      // Add each guest cart item to Firestore
      for (const item of guestCart) {
        try {
          await addOrUpdateCartItem(item);
        } catch (err) {
          console.warn('[CartContext] Failed to merge item:', item.id, err);
        }
      }
      
      // Clear guest cart after successful merge
      clearGuestCart();
      
      // Refresh cart from Firestore
      await fetchCart();
      console.log('[CartContext] Guest cart merged successfully');
    } catch (err) {
      console.error('[CartContext] Error merging guest cart:', err);
    }
  }, [fetchCart]);

  // Listen for auth changes and fetch/merge cart
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('[CartContext] Auth state changed:', user ? 'User logged in' : 'User logged out');
      
      if (user) {
        // User logged in - merge guest cart if exists, then fetch user cart
        if (!justCleared) {
          await mergeGuestCart();
          await fetchCart();
        } else {
          console.log('[CartContext] Skipping cart fetch - cart was just cleared');
        }
      } else {
        // User logged out - switch to guest cart
        console.log('[CartContext] User logged out, loading guest cart');
        const guestCart = getGuestCart();
        setCartItems(guestCart);
      }
    });
    return unsubscribe;
  }, [fetchCart, mergeGuestCart, justCleared]);
  
  // Load initial cart (guest or authenticated)
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Clear entire cart
  const clearCart = async () => {
    try {
      console.log('[CartContext] Starting cart clear...');
      
      setJustCleared(true);
      const user = auth.currentUser;
      
      if (user) {
        // Authenticated user - clear Firestore cart
        console.log('[CartContext] User authenticated, clearing Firestore cart...');
        const cartRef = collection(db, 'users', user.uid, 'cart');
        const snapshot = await getDocs(cartRef);
        console.log('[CartContext] Found', snapshot.docs.length, 'cart items to delete');
        
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        console.log('[CartContext] Firestore cart cleared successfully');
      } else {
        // Guest user - clear localStorage cart
        console.log('[CartContext] Guest user, clearing localStorage cart...');
        clearGuestCart();
      }
      
      setCartItems([]);
      console.log('[CartContext] Cart clear completed successfully');
      
      setTimeout(() => {
        setJustCleared(false);
        console.log('[CartContext] Reset justCleared flag');
      }, 2000);
    } catch (err) {
      console.error('[CartContext] Error clearing cart:', err);
      setJustCleared(false);
    }
  };

      // Cart context state

  return (
    <CartContext.Provider
      value={{
        cartItems,
        loading,
        error,
        fetchCart,
        updateCartItem,
        removeCartItem,
        addCartItem,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
} 